import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatToDate } from "../../master/FormatToDateTime";
import usePrivateLink from "../../config/usePrivateLink";
import Swal from "sweetalert2";
import { studentDelete } from "../../../api/Student";
import { getStudents } from "./Master";
import Pagination from "../../components/Pagination";
import { LoadingDiv } from "../../master/Master";
import SortableTh from "../../components/SortableTh";
import { imgBaseUrl } from "../../../api/api";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return formatToDate(value) || "—";
  } catch {
    return String(value);
  }
};

const getStudentImage = (student) => {
  const image =
    student?.profile_picture ||
    student?.student_image ||
    student?.profile_pic ||
    student?.image ||
    student?.photo;

  if (!image || typeof image !== "string") return "";

  // Already a complete image URL
  if (/^https?:\/\//i.test(image)) return image;

  // Build URL using your existing image base URL
  return `${imgBaseUrl.replace(/\/+$/, "")}/${image.replace(/^\/+/, "")}`;
};

const initials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("")
    .toUpperCase() || "ST";

const readable = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;
const errorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.message ||
  error?.message ||
  "Please try again.";

export default function StudentTable({ filters = {}, setFilters }) {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [searchInput, setSearchInput] = useState(filters?.search || "");
  const latestRequest = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Avoid an API request on every keystroke; the parent persists applied filters.
  useEffect(() => {
    setSearchInput(filters?.search || "");
  }, [filters?.search]);

  useEffect(() => {
    const next = searchInput.trim();
    if (next === (filters?.search || "")) return;
    const timer = setTimeout(() => {
      setFilters({ ...filtersRef.current, search: next, page: 1 });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, filters?.search, setFilters]);

  const loadStudents = useCallback(async (currentFilters) => {
    const requestNumber = ++latestRequest.current;
    try {
      await getStudents({
        setLoading: (value) => {
          if (requestNumber === latestRequest.current) setLoading(value);
        },
        filters: currentFilters,
        setData: (value) => {
          if (requestNumber === latestRequest.current) setData(value);
        },
      });
    } catch (error) {
      if (requestNumber === latestRequest.current) {
        setLoading(false);
        Swal.fire("Error", errorMessage(error), "error");
      }
    }
  }, []);

  useEffect(() => {
    loadStudents(filters);
    return () => {
      latestRequest.current += 1;
    };
  }, [filters, loadStudents]);

  const handleDelete = async (id, name) => {
    const confirm = await Swal.fire({
      title: "Delete student?",
      text: `Permanently delete ${name || "this student"}? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Delete student",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const response = await studentDelete(id);
      if (response?.data?.status) {
        await Swal.fire(
          "Deleted",
          response?.data?.message || "Student deleted successfully.",
          "success",
        );
        await loadStudents(filtersRef.current);
      } else {
        Swal.fire(
          "Warning",
          response?.data?.message || "Unable to delete student.",
          "info",
        );
      }
    } catch (error) {
      Swal.fire("Error", errorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const students = Array.isArray(data?.data) ? data.data : [];
  const total = Number(data?.total || 0);
  const from = students.length ? Number(data?.from || 1) : 0;
  const to = students.length
    ? Number(data?.to || from + students.length - 1)
    : 0;
  const canView = hasAccess("/student/details");
  const canEdit = hasAccess("/student/update");
  const canDelete = hasAccess("/student/delete");

  return (
    <section className="scai-students-table-module">
      <style>{`
        .scai-students-table-module{min-width:0;color:#25334d}
        .scai-students-table-module .sst-toolbar{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;padding:17px 20px;border-bottom:1px solid #e9eef6}
        .scai-students-table-module .sst-title{font-size:16px;line-height:1.35;font-weight:750;letter-spacing:-.25px;margin:0;color:#273449}
        .scai-students-table-module .sst-subtitle{font-size:12px;color:#8390a5;margin-top:3px}
        .scai-students-table-module .sst-controls{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
        .scai-students-table-module .sst-total{display:flex;align-items:center;gap:9px;border-radius:10px;background:#eff3ff;padding:9px 12px;color:#536ad1;font-size:12px;font-weight:650;white-space:nowrap}
        .scai-students-table-module .sst-total strong{font-size:15px;color:#354cac}
        .scai-students-table-module .sst-search{width:min(320px,100%);position:relative}
        .scai-students-table-module .sst-search>i{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#93a0b2;font-size:18px;pointer-events:none}
        .scai-students-table-module .sst-search input{height:40px;padding-left:39px;padding-right:34px;border-radius:10px;border:1px solid #e0e6ef;font-size:13px;box-shadow:none}
        .scai-students-table-module .sst-search input:focus{border-color:#6578e9;box-shadow:0 0 0 3px rgba(101,120,233,.10)}
        .scai-students-table-module .sst-clear{position:absolute;right:6px;top:50%;transform:translateY(-50%);background:transparent;border:0;color:#8792a5;width:28px;height:28px;border-radius:7px}
        .scai-students-table-module .sst-clear:hover{background:#f0f3f8}
        .scai-students-table-module .sst-results{padding:10px 20px;background:#fbfcff;border-bottom:1px solid #edf0f5;color:#79869a;font-size:12px;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
        .scai-students-table-module .sst-results strong{color:#35445d}
        .scai-students-table-module .sst-scroll{width:100%;overflow-x:auto}
        .scai-students-table-module .sst-table{width:100%;min-width:1010px;border-collapse:separate;border-spacing:0;margin:0;font-size:12.5px}
        .scai-students-table-module .sst-table thead th{background:#f6f8fc;color:#66758d;font-size:11px;font-weight:750;letter-spacing:.025em;text-transform:uppercase;padding:14px 13px;white-space:nowrap;border-bottom:1px solid #e7edf5}
        .scai-students-table-module .sst-table tbody td{padding:13px;border-bottom:1px solid #edf0f5;vertical-align:middle;color:#3a4961;background:#fff}
        .scai-students-table-module .sst-table tbody tr:hover td{background:#fafbff}
        .scai-students-table-module .sst-table tbody tr:last-child td{border-bottom:0}
        .scai-students-table-module .sst-index{color:#929caf;font-size:12px;font-weight:650}
        .scai-students-table-module .sst-person{display:flex;align-items:center;gap:11px;min-width:190px}
.scai-students-table-module .sst-avatar {
  flex: none;
  width: 39px;
  height: 39px;
  border-radius: 12px;
  overflow: hidden;
  background: #eaf0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #526bd4;
  font-weight: 800;
  font-size: 12px;
}

.scai-students-table-module .sst-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
          .scai-students-table-module .sst-name{font-weight:750;color:#3349a1;text-decoration:none;display:block;line-height:1.4;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .scai-students-table-module .sst-name:hover{text-decoration:underline;color:#263b92}
        .scai-students-table-module .sst-muted{color:#8793a5;font-size:11.5px;line-height:1.5}
        .scai-students-table-module .sst-strong{font-weight:650;color:#35435b}
        .scai-students-table-module .sst-phone{display:inline-flex;align-items:center;gap:6px;color:#506eaa;white-space:nowrap;font-weight:650}
        .scai-students-table-module .sst-class{display:flex;flex-direction:column;align-items:flex-start;gap:3px;max-width:190px}
        .scai-students-table-module .sst-class span{display:block;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .scai-students-table-module .sst-class strong{font-size:12px;font-weight:700;color:#4b5fc4}
        .scai-students-table-module .sst-date{font-weight:650;color:#33445f;white-space:nowrap}
        .scai-students-table-module .sst-actions{display:flex;justify-content:flex-end;gap:6px}
        .scai-students-table-module .sst-action{display:inline-flex;align-items:center;justify-content:center;width:33px;height:33px;padding:0;border:1px solid #e1e7f0;background:#fff;color:#61728d;border-radius:9px;text-decoration:none;transition:.15s}
        .scai-students-table-module .sst-action i{font-size:16px}
        .scai-students-table-module .sst-action:hover{background:#f0f3ff;border-color:#cbd5fa;color:#5065cc}
        .scai-students-table-module .sst-action.sst-danger:hover{background:#fff0f0;border-color:#ffcdcd;color:#d23535}
        .scai-students-table-module .sst-empty{text-align:center;padding:55px 20px!important}
        .scai-students-table-module .sst-empty i{font-size:42px;color:#b0bdd2}
        .scai-students-table-module .sst-empty h6{margin:12px 0 5px;font-weight:700;color:#33445d}
        .scai-students-table-module .sst-empty p{margin:0;color:#8190a4;font-size:12px}
        .scai-students-table-module .sst-pagination{padding:12px 20px;border-top:1px solid #edf0f5}
        @media(max-width:767px){.scai-students-table-module .sst-toolbar{padding:15px 13px}.scai-students-table-module .sst-controls{width:100%}.scai-students-table-module .sst-search{width:100%;flex:1;min-width:185px}.scai-students-table-module .sst-total{padding:8px 10px}.scai-students-table-module .sst-results{padding:10px 13px}.scai-students-table-module .sst-pagination{padding:12px}.scai-students-table-module .sst-table thead th,.scai-students-table-module .sst-table tbody td{padding:11px}}
        @media(prefers-reduced-motion:reduce){.scai-students-table-module .sst-action{transition:none}}
      `}</style>

      {/* <div className="sst-toolbar">
        <div>
          <h5 className="sst-title"><i className="ri-group-line me-2 text-primary" />Student Records</h5>
          <div className="sst-subtitle">Review admission details, classes, and student profiles</div>
        </div>
        <div className="sst-controls">
          <div className="sst-total"><i className="ri-team-line" /><span>Total</span><strong>{total.toLocaleString("en-IN")}</strong></div>
          <div className="sst-search">
            <i className="ri-search-line" />
            <input type="search" className="form-control" aria-label="Search students" placeholder="Search name, mobile or reg. no." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            {searchInput && <button type="button" className="sst-clear" aria-label="Clear search" onClick={() => setSearchInput("")}><i className="ri-close-line" /></button>}
          </div>
        </div>
      </div> */}

      <div className="sst-results">
        <i className="ri-list-check-2" />
        {loading ? (
          "Loading students…"
        ) : (
          <span>
            Showing{" "}
            <strong>
              {from}–{to}
            </strong>{" "}
            of <strong>{total.toLocaleString("en-IN")}</strong> students
          </span>
        )}
        {(filters?.start_date || filters?.end_date) && (
          <span>• Admission date filter applied</span>
        )}
        <div className="sst-search ms-auto">
          <i className="ri-search-line" />
          <input
            type="search"
            className="form-control"
            aria-label="Search students"
            placeholder="Search name, mobile or reg. no."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className="sst-clear"
              aria-label="Clear search"
              onClick={() => setSearchInput("")}
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
      </div>

      <div className="sst-scroll">
        <table className="table sst-table" aria-label="Student directory">
          <thead>
            <tr>
              <th style={{ width: 52 }}>#</th>
              <SortableTh
                label="Student / Reg. No."
                field="name"
                filters={filters}
                setFilters={setFilters}
              />
              <SortableTh
                label="Parents"
                field="father_name"
                filters={filters}
                setFilters={setFilters}
              />
              <SortableTh
                label="Mobile"
                field="phone"
                filters={filters}
                setFilters={setFilters}
              />
              <SortableTh
                label="DOB"
                field="dob"
                filters={filters}
                setFilters={setFilters}
              />
              <th>Batch / Course</th>
              <SortableTh
                label="Admission Date"
                field="admission_date"
                filters={filters}
                setFilters={setFilters}
              />
              <th style={{ width: 126, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {students.length ? (
              students.map((item, index) => {
                const batch = item?.course_and_fee_info?.batch?.title;
                const course = item?.course_and_fee_info?.course?.title;
                return (
                  <tr key={item.id}>
                    <td>
                      <span className="sst-index">{from + index}</span>
                    </td>
                    <td>
                      <div className="sst-person">
                        {(() => {
                          const studentImage = getStudentImage(item);

                          return (
                            <div
                              className="sst-avatar"
                              aria-label={item?.name || "Student"}
                            >
                              {studentImage && (
                                <img
                                  src={studentImage}
                                  alt={item?.name || "Student"}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fallback =
                                      e.currentTarget.nextElementSibling;
                                    if (fallback)
                                      fallback.style.display = "flex";
                                  }}
                                />
                              )}

                              <span
                                style={{
                                  display: studentImage ? "none" : "flex",
                                  width: "100%",
                                  height: "100%",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {initials(item?.name)}
                              </span>
                            </div>
                          );
                        })()}
                        <div style={{ minWidth: 0 }}>
                          {canView ? (
                            <Link
                              className="sst-name"
                              to={`/student/details?id=${item.id}`}
                            >
                              {readable(item?.name)}
                            </Link>
                          ) : (
                            <span
                              className="sst-name"
                              style={{ color: "#35435b" }}
                            >
                              {readable(item?.name)}
                            </span>
                          )}
                          <div className="sst-muted">
                            Reg. No: {readable(item?.registration_no)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="sst-strong">
                        {readable(item?.father_name)}
                      </div>
                      <div className="sst-muted">
                        {readable(item?.mother_name)}
                      </div>
                    </td>
                    <td>
                      <span className="sst-phone">
                        <i className="ri-phone-line" />
                        {readable(item?.phone)}
                      </span>
                    </td>
                    <td>
                      <span className="sst-date">{formatDate(item?.dob)}</span>
                    </td>
                    <td>
                      <div className="sst-class">
                        <strong title={batch || ""}>{readable(batch)}</strong>
                        <span className="sst-muted" title={course || ""}>
                          {readable(course)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="sst-date">
                        <i className="ri-calendar-check-line me-1 text-muted" />
                        {formatDate(item?.admission_date)}
                      </span>
                    </td>
                    <td>
                      <div className="sst-actions">
                        {canView && (
                          <Link
                            className="sst-action"
                            title="View student"
                            aria-label={`View ${item?.name || "student"}`}
                            to={`/student/details?id=${item.id}`}
                          >
                            <i className="ri-eye-line" />
                          </Link>
                        )}
                        {canEdit && (
                          <Link
                            className="sst-action"
                            title="Edit student"
                            aria-label={`Edit ${item?.name || "student"}`}
                            to={`/student/update?id=${item.id}`}
                          >
                            <i className="ri-edit-line" />
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="sst-action sst-danger"
                            title="Delete student"
                            aria-label={`Delete ${item?.name || "student"}`}
                            onClick={() => handleDelete(item.id, item.name)}
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        )}
                        {!canView && !canEdit && !canDelete && (
                          <span className="sst-muted">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="sst-empty">
                  <i className="ri-user-search-line" />
                  <h6>No students found</h6>
                  <p>
                    Try changing the admission date, month, status, or search
                    filters.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sst-pagination">
        <Pagination data={data} setFilters={setFilters} filters={filters} />
      </div>
      {loading && <LoadingDiv />}
    </section>
  );
}
